'use client';

import { useEffect, useState } from 'react';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  msUntilNextAeBoundary,
  resolveAeDeliveryWindow,
  type AeDeliveryWindowState,
} from './deliveryWindow';
import type { AeEmirateGroup } from './emirates';

/**
 * Live Asia/Dubai delivery window for one emirate group (see
 * ./deliveryWindow.ts), re-synced via a timeout armed to the next GST
 * boundary (10:00 / 13:00 / 17:00 / midnight — the union across groups, so
 * changing `group` never needs a different timer schedule) instead of
 * polling, plus a `visibilitychange` re-sync so a laptop waking past a
 * boundary corrects immediately rather than waiting for the timer. Callers
 * share no state: each instance owns its own state and timer. They agree on
 * the window because `resolveAeDeliveryWindow` is a pure function of
 * wall-clock time and group, not because of any shared store. Prefer
 * ./useAeDeliveryEta.ts, which derives `group` from the selected emirate.
 *
 * Hydration safety: `serverNow` is stamped once per request
 * (lib/region.server.ts) and flows through RegionLocaleContext identically
 * to server render and first client paint, so the initial window never
 * mismatches.
 *
 * Returns 'error' when the resolver throws (no Asia/Dubai Intl data) — the
 * "couldn't check delivery timelines" row of the ops sheet.
 *
 * @param enabled - Pass the caller's AE visibility gate. When false, the
 * effect returns immediately: no timer, no `visibilitychange` listener, no
 * re-render risk for non-AE visitors. The hook itself must still be called
 * unconditionally (rules of hooks) — only its internal effect is gated.
 */
export const useAeDeliveryWindow = (
  enabled: boolean,
  group: AeEmirateGroup,
): AeDeliveryWindowState => {
  const { serverNow } = useRegionLocale();
  const [windowState, setWindowState] = useState<AeDeliveryWindowState>(() => {
    try {
      return resolveAeDeliveryWindow(group, new Date(serverNow));
    } catch {
      return 'error';
    }
  });

  useEffect(() => {
    if (!enabled) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const sync = () => {
      clearTimeout(timeoutId);
      const now = new Date();
      try {
        setWindowState(resolveAeDeliveryWindow(group, now));
        timeoutId = setTimeout(sync, msUntilNextAeBoundary(now));
      } catch {
        setWindowState('error');
      }
    };
    sync();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, group]);

  return windowState;
};
